import { skipToken, useQuery } from '@tanstack/react-query';

import { useCurrentBusiness } from '@/hooks/use-current-business';
import { businessQueryKey, getBusiness } from '@/lib/api/businesses';
import { listTiers, TIERS_QUERY_KEY } from '@/lib/api/tiers';
import { hasActiveAccess, tierHasFeature, type Feature } from '@/lib/entitlements';
import type { SubscriptionStatus } from '@/types';

interface Entitlements {
  isPending: boolean;
  tierSlug?: string;
  subscriptionStatus?: SubscriptionStatus;
  trialEndsAt: string | null;
  stripeCustomerId: string | null;
  /** Whether the subscription lifecycle currently grants access, regardless of feature. */
  hasAccess: boolean;
  has: (feature: Feature) => boolean;
}

/**
 * Resolves the active business's subscription tier and exposes feature
 * checks against it. Fail-closed: while either lookup is pending, `has`
 * returns false so tier-gated UI stays hidden until entitlement is known.
 * `has` also accounts for subscription lifecycle (trial expiry, cancellation)
 * via `hasActiveAccess`, not just tier slug.
 */
export function useEntitlements(): Entitlements {
  const business = useCurrentBusiness();
  const businessId = business?.business_id;

  const businessQuery = useQuery({
    queryKey: businessQueryKey(businessId ?? ''),
    queryFn: businessId ? () => getBusiness(businessId) : skipToken,
  });

  const tiersQuery = useQuery({
    queryKey: TIERS_QUERY_KEY,
    queryFn: listTiers,
  });

  const isPending = businessQuery.isPending || tiersQuery.isPending;

  const tierSlug = tiersQuery.data?.find(
    (tier) => tier.id === businessQuery.data?.tier_id,
  )?.slug;

  const subscriptionStatus = businessQuery.data?.subscription_status;
  const trialEndsAt = businessQuery.data?.trial_ends_at ?? null;
  const hasAccess = isPending
    ? false
    : hasActiveAccess(subscriptionStatus ?? 'canceled', trialEndsAt);

  return {
    isPending,
    tierSlug,
    subscriptionStatus,
    trialEndsAt,
    stripeCustomerId: businessQuery.data?.stripe_customer_id ?? null,
    hasAccess,
    has: (feature) =>
      isPending ? false : hasAccess && tierHasFeature(tierSlug, feature),
  };
}
